import { LngLatBounds, LngLatBoundsLike, Map as MapLibre } from 'maplibre-gl';
import { MapContent, MapContentBase } from '../MapContent';
import { MapContentVectorSource } from '../MapContentVectorSource';
import { TrackStyles } from '../../../../../configuration/model/TrackStyles';
import { StormTrackLayer } from './StormTrackLayer';
import { MapTools } from '../../../../tools/MapTools';
import { StormTrackArrowsLayer } from './StormTrackArrowsLayer';
import { MapContentSource } from '../MapContentSource';
import { StormMarkerShadowLayer } from './StormMarkerShadowLayer';
import { StormMarkerCircleLayer } from './StormMarkerCircleLayer';
import { StormMarkerSymbolLayer } from './StormMarkerSymbolLayer';
import { Feature, FeatureCollection } from 'geojson';
import { StormInfo, StormShift } from '../../../../models/storm.model';
import { environment } from '../../../../../../environments/environment';
import { ConfigurationModel } from '../../../../models/configuration.model';
import { FetchSource, PMTiles, Protocol } from 'pmtiles';
import { StormMapDataService } from '../../../../../pages/insights/components/storm-report/storm-map-data.service';


export class StormMapContent extends MapContentBase implements MapContent {

  private tracksSource: MapContentVectorSource;
  private readonly tracksLayer: StormTrackLayer;
  private readonly tracksArrowLayer: StormTrackArrowsLayer;
  private readonly stormLengthSeconds: number;
  private markersSource: MapContentSource = new MapContentSource('storm-markers-source');
  private markerShadowLayer: StormMarkerShadowLayer;
  private markerCircleLayer: StormMarkerCircleLayer;
  private markerSymbolLayer: StormMarkerSymbolLayer;
  private bounds: LngLatBoundsLike;

  // animation properties
  private speedFactor = 10; // 1 = normal time, 10 = 10x faster
  private animation; // to store and cancel the animation
  private animationStartTime = 0;
  private progress = 0; // progress = timestamp - startTime
  private resetTime = false; // indicator of whether time reset is needed for the animation
  private shiftLatestIndex = new Map<number, number>();

  constructor(
    configuration: ConfigurationModel,
    trackStyles: TrackStyles,
    private stormShifts: StormShift[],
    private storm: StormInfo,
    private playbackIndex: any,
    private stormMapDataService: StormMapDataService,
    private currentTimestampUpdated: (unixtime: number) => void,
  ) {
    super();
    this.stormLengthSeconds = this.storm.durationInSeconds;

    const tenantId = configuration.id;
    const pmTilesUrl = `${environment.filesBaseUrl}storms/${tenantId}/${this.storm.id}.track.pmtiles`;

    // fallback bounding box - tenant region
    this.bounds = configuration.region.reduce(
      (bounds, latLngModel) => {
        return bounds.extend([latLngModel.lng, latLngModel.lat]);
      },
      new LngLatBounds(configuration.region[0], configuration.region[0])
    );
    // get bounding box from PMTiles
    const source = new FetchSource(pmTilesUrl, new Headers(), 'include');
    const pmTiles = new PMTiles(source);
    pmTiles.getHeader().then(header => {
      this.bounds = [[header.minLon, header.minLat], [header.maxLon, header.maxLat]];
    });

    // set PMTiles source
    this.tracksSource = new MapContentVectorSource(
      'storm-tracks-source',
      `pmtiles://${pmTilesUrl}`,
      pmTiles,
    );

    this.tracksLayer = new StormTrackLayer(this.tracksSource, trackStyles, stormShifts);
    this.tracksArrowLayer = new StormTrackArrowsLayer(this.tracksSource);

    this.markerShadowLayer = new StormMarkerShadowLayer(this.markersSource);
    this.markerCircleLayer = new StormMarkerCircleLayer(this.markersSource);
    this.markerSymbolLayer = new StormMarkerSymbolLayer(this.markersSource);
  }

  load(map: MapLibre, protocol: Protocol, zoomToFeatures: boolean) {
    this.map = map;
    this.protocol = protocol;
    this.firstSymbolLayerId = this.getFirstSymbolLayerId();

    this.tracksSource.updateMap(map, protocol);
    this.map.addLayer(this.tracksLayer.toLayerSpecification(), this.firstSymbolLayerId);
    this.map.addLayer(this.tracksArrowLayer.toLayerSpecification(), this.firstSymbolLayerId);

    this.markersSource.updateMap(this.map);
    this.map.addLayer(this.markerShadowLayer.toLayerSpecification());
    this.map.addLayer(this.markerCircleLayer.toLayerSpecification());
    this.map.addLayer(this.markerSymbolLayer.toLayerSpecification());

    if (zoomToFeatures) {
      this.map.fitBounds(this.bounds, { padding: 20 });
    }
  }

  unload() {
    this.map.removeLayer(this.tracksArrowLayer.layerId);
    this.map.removeLayer(this.tracksLayer.layerId);
    this.map.removeSource(this.tracksSource.sourceId);

    this.map.removeLayer(this.markerSymbolLayer.layerId);
    this.map.removeLayer(this.markerCircleLayer.layerId);
    this.map.removeLayer(this.markerShadowLayer.layerId);
    this.map.removeSource(this.markersSource.sourceId);

    this.map = null;
  }

  // params: percentage
  filterPercent(pctFrom: number, pctTo: number) {
    const timeFrom = this.getAbsTime(pctFrom);
    const timeTo = this.getAbsTime(pctTo);
    MapTools.onMap(this.map, () => {
      this.tracksLayer.filterByTime(this.map, timeFrom, timeTo);
    });
  }

  // external trigger to jump to timestamp
  filterTo(timeTo: number) {
    this.progress = (timeTo - this.storm.startUnix) / this.speedFactor * 1000;
    MapTools.onMap(this.map, () => {
      this.tracksLayer.filterByTime(this.map, null, timeTo);
      this.tracksArrowLayer.filterByTime(this.map, null, timeTo);

      this.markersSource.clearFeatures();
      this.stormShifts
        .filter(shift => shift.start <= timeTo && shift.end >= timeTo)
        .forEach(shift => {
          const shiftPlaybackIndex = this.playbackIndex[shift.id];
          if (shiftPlaybackIndex) {
            const p = this.findLatestPosition(
              shiftPlaybackIndex,
              timeTo
            );
            const r = this.findLatestPositionIndex(
              shiftPlaybackIndex,
              timeTo
            );

            if (p) {
              const [unixtime, lon, lat, heading] = p;
              this.markersSource.addFeature(
                this.createMarkerFeature(
                  [lon, lat],
                  {
                    unixtime,
                    heading,
                  },
                )
              );
            }
            if (r >= 0) {
              this.shiftLatestIndex.set(shift.id, r);
            }
          } else {
           // missing data for shift in the playback index
           console.warn('Missing data in playback index for shift=', shift.id);
          }
        });
      this.markersSource.updateMap(this.map);
    });
  }

  findLatestPosition(points: number[][], timestamp: number): number[] {
    let lo = 0;
    let hi = points.length - 1;

    let best = null;

    while (lo <= hi) {

      const mid = (lo + hi) >> 1;

      if (points[mid][0] <= timestamp) {
        best = points[mid];
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    return best;
  }

  findLatestPositionIndex(points: number[][], timestamp: number): number {
    let lo = 0;
    let hi = points.length - 1;

    let best = null;
    let bestIndex = -1;

    while (lo <= hi) {

      const mid = (lo + hi) >> 1;

      if (points[mid][0] <= timestamp) {
        best = points[mid];
        bestIndex = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }

    return bestIndex;
  }

  createMarkerFeature(coordinates: number[], properties: {heading: number, unixtime: number}): Feature {
    return {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates,
      },
      properties,
    };
  }

  private getAbsTime(relativePosition: number): number {
    // unknown start or length
    if (!this.storm.startUnix || !this.stormLengthSeconds) {
      return null;
    }
    // do not filter start or end at all
    if (relativePosition === 0 || relativePosition === 100) {
      return null;
    }
    return Math.round(this.storm.startUnix + relativePosition / 100 * this.stormLengthSeconds);
  }

  changeAnimationSpeed(speedFactor: number) {
    this.speedFactor = speedFactor;
  }

  playAnimation() {
    // eslint-disable-next-line no-console
    console.log('playing animation');
    this.animationStartTime = performance.now();
    this.resetTime = true;
    this.animateLine(this.animationStartTime);
  }

  // DOMHighResTimeStamp parameter
  private animateLine(timestamp) {
    if (this.resetTime) {
      // resume previous progress
      this.animationStartTime = timestamp - this.progress;
      this.resetTime = false;
    } else {
      this.progress = timestamp - this.animationStartTime;
    }

    if (this.progress / 1000 * this.speedFactor > this.stormLengthSeconds) {
      // eslint-disable-next-line no-console
      console.log('Storm end has been reached.');
      this.stormMapDataService.sendStormEndReached();
      return;
    }

    const calcNextPointTime = this.progress / 1000 * this.speedFactor;
    this.markersSource.clearFeatures();
    this.currentTimestampUpdated(this.storm.startUnix + calcNextPointTime);
    this.stormShifts
      .filter(shift => shift.start <= this.storm.startUnix + calcNextPointTime && shift.end >= this.storm.startUnix + calcNextPointTime)
      .forEach(shift => {
        const shiftPlaybackIndex = this.playbackIndex[shift.id];
        if (shiftPlaybackIndex) {
          if (this.shiftLatestIndex.get(shift.id) === undefined || shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id)]) {
            const nextPointTime = shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id) ?? 0][0] - this.storm.startUnix;
            if (nextPointTime < calcNextPointTime) {
              // use the latest point and move index
              this.markersSource.addFeature(
                this.createMarkerFeature(
                  [
                    shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id) ?? 0][1],
                    shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id) ?? 0][2],
                  ],
                  {
                    unixtime: nextPointTime,
                    heading: shiftPlaybackIndex[this.shiftLatestIndex.get(shift.id) ?? 0][3],
                  },
                )
              );
              this.shiftLatestIndex.set(shift.id, (this.shiftLatestIndex.get(shift.id) ?? 0) + 1);
              this.tracksLayer.filterByTime(this.map, null, this.storm.startUnix + nextPointTime);
              this.tracksArrowLayer.filterByTime(this.map, null, this.storm.startUnix + nextPointTime);
            } else {
              // keeping index and interpolate
              const lastIndex = this.shiftLatestIndex.get(shift.id) ?? 0;
              if (lastIndex > 0) {
                const from = shiftPlaybackIndex[lastIndex - 1];
                const fromLng = from[1];
                const fromLat = from[2];
                const fromTime = from[0] - this.storm.startUnix;
                const to = shiftPlaybackIndex[lastIndex];
                const toLng = to[1];
                const toLat = to[2];
                const toTime = to[0] - this.storm.startUnix;
                const timeDiff = toTime - fromTime;
                const timeBetweenPoints = calcNextPointTime - fromTime;
                const pctBetweenPoints = timeBetweenPoints / timeDiff;
                const interpolatedLng = fromLng + (toLng - fromLng) * pctBetweenPoints;
                const interpolatedLat = fromLat + (toLat - fromLat) * pctBetweenPoints;
                // this.vehicleMarkerClicked(
                // this.shiftPointFeatures[this.latestIndex - 1] as PointFeature, calcNextPointTime + this.storm.startUnix
                // );
                this.markersSource.addFeature(
                  this.createMarkerFeature(
                    [
                      interpolatedLng,
                      interpolatedLat,
                    ],
                    {
                      unixtime: nextPointTime,
                      heading: to[3],
                    },
                  )
                );
              }
            }
          }
        } else {
          // skipping shift animation as it has no data in playback index
        }
      });
    this.markersSource.updateMap(this.map);


    // Request the next frame of the animation.
    this.animation = requestAnimationFrame(time => this.animateLine(time));
  }

  stopAnimation() {
    // eslint-disable-next-line no-console
    console.log('stopping animation...');
    this.shiftLatestIndex.clear();
    this.progress = 0;
    cancelAnimationFrame(this.animation);
  }
}
